import React from "react";
import { useShallow } from "zustand/react/shallow";
import { motion, AnimatePresence } from "framer-motion";
import {
    GameColor,
    Event,
    RoomPlayerAction
} from "@uno/protocol";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { useWebHaptics } from "web-haptics/react";
import { AppContext } from "context";
import useLocalStorage from "hook/useLocalStorage";
import useStoreRoom from "store/useStoreRoom";
import useStoreRoomPlayer from "store/useStoreRoomPlayer";

const DiscardColorSelector = () => {
    const { socket } = React.useContext(AppContext);
    const { trigger } = useWebHaptics();
    const [hapticEnabled] = useLocalStorage<boolean>("LASTDOTCARDS.HAPTIC", true);
    const roomId = useStoreRoom(state => state.room?.id);
    const roomPlayer = useStoreRoomPlayer(useShallow(state => ({ action: state.roomPlayer?.action })));

    const handleColorChoice = React.useCallback((color: GameColor) => {
        if(hapticEnabled) {
            trigger("light");
        }

        socket?.emit(Event.RoomPlayerChooseColor, { roomId: roomId, color });
    }, [socket, roomId]);

    if(roomPlayer?.action !== RoomPlayerAction.ChoosingColor) {
        return null;
    }

    return (
        <AnimatePresence mode="wait">
            <motion.div 
                key={roomPlayer?.action}
                initial={{ opacity: 0, rotate: 0, scale: 1 }}
                animate={{ 
                    scale: 1.15,
                    opacity: 1, 
                    rotate: 360,
                    transition: {
                        opacity: { 
                            duration: 0.25,
                        },
                        rotate: { 
                            duration: 0.5, 
                            ease: "easeInOut" 
                        }
                    }
                }}
                exit={{ opacity: 0 }}
                className={"absolute z-[25] w-full h-full bg-white rounded-full border-4"}
                style={{
                    borderColor: "rgba(100, 100, 100, 1)"
                }}
            >
                <div 
                    className="absolute top-0 left-0 w-1/2 h-1/2 rounded-tl-full cursor-pointer hover:brightness-110"
                    onClick={() => handleColorChoice(GameColor.Red)}
                    style={{ background: "var(--red-fill-color)" }}
                ></div>

                <div
                    className="absolute top-0 right-0 w-1/2 h-1/2 rounded-tr-full cursor-pointer hover:brightness-110"
                    onClick={() => handleColorChoice(GameColor.Blue)}
                    style={{ background: "var(--blue-fill-color)" }}
                ></div>

                <div
                    className="absolute bottom-0 left-0 w-1/2 h-1/2 rounded-bl-full cursor-pointer hover:brightness-110"
                    onClick={() => handleColorChoice(GameColor.Yellow)}
                    style={{ background: "var(--yellow-fill-color)" }}
                ></div>

                <div
                    className="absolute bottom-0 right-0 w-1/2 h-1/2 rounded-br-full cursor-pointer hover:brightness-110"
                    onClick={() => handleColorChoice(GameColor.Green)}
                    style={{ background: "var(--green-fill-color)" }}
                ></div>
            </motion.div>
        </AnimatePresence>
    );
};

export default DiscardColorSelector;